import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import Button from '@Components/Buttons/Button'
import ModalPortal from '@App/kit/components/Popups/ModalPortal'
import PointPreloader from '@App/kit/components/Preloaders/PointPreloader'
import { StoresContext } from '@App/contexts'
import { PaginationBody } from '@Entities/pagination'
import ContentHeader from '@App/kit/components/ContentHeader'
import RootStore from '@App/stores/root-store'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import SearchPanel from '@Components/Search/SearchPanel'
import MatchSearchResults from '@Scenes/RightsholderScene/MatchClaim/Match/MatchWorkSelection/MatchSearchResults'
import MatchWorkSelectionHeader from '@Scenes/RightsholderScene/MatchClaim/Match/MatchWorkSelection/MatchWorkSelectionHeader'
import SubmitMatchPopup from '@Components/MatchComponents/SubmitMatchPopup'
import Pluralize from 'pluralize'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { useLocation } from 'react-router'
import WorkSearchStore from '@App/stores/search/work/work-search-store'
import { Works } from '@Entities/work/works'
import MatchingStore from '@App/stores/rightsholder/match/matching-store'
import useSubmitMatch from '@Scenes/RightsholderScene/MatchClaim/Match/use-submit-match'

interface MatchWorkSelectionProps {
    matchingStore: MatchingStore
    goToStep: (step: number) => void
}

const MatchWorkSelection = (props: MatchWorkSelectionProps) => {
    const { catalogSearchStore, songwriterCatalogSearchStore, workSearchStore } = useContext<RootStore>(StoresContext)

    const { goToStep, matchingStore } = props

    const location = useLocation()

    const isSongwriterHub = location.pathname.startsWith('/songwriter')

    const submitMatch = useSubmitMatch()

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
    const [worksSelection, setWorksSelection] = useState<string>('')
    const [resultsData, setResultsData] = useState<Works>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [matchWorkSearchCriteriaStore, setMatchWorkSearchCriteriaStore] = useState<SearchCriteriaStore<WorkSearch>>()

    const selectWork = (songCode: string, title: string) => {
        setWorksSelection(songCode)
        matchingStore.setSelectedWorkTitle(title)
        setModalIsOpen(true)
    }

    const handleSearch = async (pagination: PaginationBody = { page: 1, limit: 5 }) => {
        setIsLoading(true)

        if (isSongwriterHub) {
            await songwriterCatalogSearchStore.getWorksRightsHolder(
                pagination,
                workSearchStore.matchWorksSelectionSearchQuery
            )
            setResultsData(songwriterCatalogSearchStore.worksRightsHolder)
        } else {
            await catalogSearchStore.getWorksRightsHolder(pagination, workSearchStore.matchWorksSelectionSearchQuery)
            setResultsData(catalogSearchStore.worksRightsHolder)
        }

        setIsLoading(false)
    }

    const handleSearchWithNewCriteria = (searchCriteria: WorkSearch) => {
        workSearchStore.setMatchWorksSelectionSearchQuery(searchCriteria)
        handleSearch().then()
    }

    const handleSubmitMatch = async () => {
        setIsLoading(true)

        await submitMatch(worksSelection, matchingStore.selectedUnmatchedRecordingGroups)

        setIsLoading(false)
        goToStep(3)
    }

    useEffect(() => {
        setMatchWorkSearchCriteriaStore(WorkSearchStore.createMatchWorkSearchCriteriaStore())
    }, [])

    const numRecGroupsMatching = matchingStore.selectedUnmatchedRecordingGroups.length

    return (
        <>
            <title>Matching Tool</title>
            <div className="match-selection-wrapper">
                <div className="global-container">
                    <ContentHeader
                        title="Match Usage - Select Work"
                        descriptionElement={<MatchWorkSelectionHeader />}
                    />

                    <div className="match-work-selection-works">
                        <p className="instructions">
                            Let&apos;s search <strong>your catalog</strong> for a work to match the
                            <strong> {Pluralize('selected recording group', numRecGroupsMatching, true)}</strong> to.
                        </p>
                        {matchWorkSearchCriteriaStore && (
                            <SearchPanel
                                searchCriteriaStore={matchWorkSearchCriteriaStore}
                                canAddCriteria={false}
                                onSearch={handleSearchWithNewCriteria}
                                orientation="row"
                            />
                        )}
                        {isLoading ? (
                            <PointPreloader />
                        ) : resultsData ? (
                            <MatchSearchResults
                                resultsData={resultsData}
                                selectWork={selectWork}
                                handleSearch={handleSearch}
                            />
                        ) : (
                            <div className="pre-search">
                                <p>Enter two (2) search criteria to see results</p>
                            </div>
                        )}
                    </div>
                    <div className="bottom-nav">
                        <Button mainColor type="secondary" onClick={() => goToStep(1)}>
                            Back
                        </Button>
                    </div>
                    <ModalPortal isOpen={modalIsOpen}>
                        <SubmitMatchPopup
                            isLoading={isLoading}
                            onCancel={() => setModalIsOpen(false)}
                            onConfirm={handleSubmitMatch}
                        />
                    </ModalPortal>
                </div>
            </div>
        </>
    )
}

export default observer(MatchWorkSelection)
